import { ref } from 'vue'
import { defineStore } from 'pinia'

import {
  sendRequest,
  resetRequestStatus,
  convertApiDataFromStore,
} from '@services/api/requestStoreUtils'

import {
  type SendRequestConfig,
  type EndpointConfig,
  type ResetRequestStatusConfig,
} from '@services/api/declarations'

import { type ApiMessagesStoreShape } from '@services/Communications/declarations'

// ***
// COMMUNICATIONS STORE
// ***

export const useApiMessagesStore = defineStore('apiMessages', () => {
  const store = useApiMessagesStore()

  // ***
  // STATE
  // ***
  const getMailboxes = ref<ApiMessagesStoreShape['getMailboxes']>()
  const getMailbox = ref<ApiMessagesStoreShape['getMailbox']>()
  const postMailbox = ref<ApiMessagesStoreShape['postMailbox']>()
  const patchMailbox = ref<ApiMessagesStoreShape['patchMailbox']>()
  const deleteMailbox = ref<ApiMessagesStoreShape['deleteMailbox']>()
  const getMessageThreads = ref<ApiMessagesStoreShape['getMessageThreads']>()

  const deleteMessageThread = ref<ApiMessagesStoreShape['deleteMessageThread']>()
  const postCreateThread = ref<ApiMessagesStoreShape['postCreateThread']>()
  const getThreadDetails = ref<ApiMessagesStoreShape['getThreadDetails']>()
  const getThreadConversations = ref<ApiMessagesStoreShape['getThreadConversations']>()
  const getThreadSingleConversation = ref<ApiMessagesStoreShape['getThreadSingleConversation']>()
  const getThreadParticipants = ref<ApiMessagesStoreShape['getThreadParticipants']>()
  const patchRestoreThread = ref<ApiMessagesStoreShape['patchRestoreThread']>()

  const getUnreadMessages = ref<ApiMessagesStoreShape['getUnreadMessages']>()

  const getAccesibleParticipants = ref<ApiMessagesStoreShape['getAccesibleParticipants']>()
  const getAllowedRecipients = ref<ApiMessagesStoreShape['getAllowedRecipients']>()
  const getThreadBroadcastMessages = ref<ApiMessagesStoreShape['getThreadBroadcastMessages']>()
  const postCreateMessageInThread = ref<ApiMessagesStoreShape['postCreateMessageInThread']>()
  const postClubContactMessage = ref<ApiMessagesStoreShape['postClubContactMessage']>()

  return {
    // State
    getMailboxes,
    getMailbox,
    postMailbox,
    patchMailbox,
    deleteMailbox,
    getMessageThreads,
    deleteMessageThread,
    postCreateThread,
    getThreadDetails,
    getThreadConversations,
    getThreadSingleConversation,
    getThreadParticipants,
    patchRestoreThread,
    getUnreadMessages,
    getAccesibleParticipants,
    getAllowedRecipients,
    getThreadBroadcastMessages,
    postCreateMessageInThread,
    postClubContactMessage,

    // Actions
    sendRequest: <Config>(
      config: SendRequestConfig<Config>,
    ): ReturnType<typeof sendRequest> => sendRequest(store, config),
    resetRequestStatus: (
      storeKey: ResetRequestStatusConfig['storeKey'],
      kind: ResetRequestStatusConfig['kind'],
    ): ReturnType<typeof resetRequestStatus> => resetRequestStatus(store, storeKey, kind),
    convertApiDataFromStore: (
      storeKey: EndpointConfig['storeKey'],
      result: Record<string, unknown>,
    ): ReturnType<typeof convertApiDataFromStore> => convertApiDataFromStore(store, storeKey, result),
  }
})
